import { z } from 'zod';
import {
  ANALYTICS_AUTH_FAILURE_KINDS,
  ANALYTICS_DEFAULT_WINDOW,
  ANALYTICS_GRAINS,
  ANALYTICS_WINDOWS,
} from '../constants/analytics.constant';
import { ACCESS_ROLES } from '../constants/identity.constant';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE USAGE ANALYTICS PAGE'S WIRE FORMAT. [ANALYTICS §5]
 *
 *  ⚠️ ONE SOURCE FOR BOTH ENDS. `GET /analytics` builds this shape and the web page reads it, so
 *  the DTO is `z.infer` of what is below and nothing is hand-written — CLAUDE.md §6.
 *
 *  ⚠️ EVERY SECTION IS `{ data } | { failure: 'UNAVAILABLE' }`, AND THAT IS THE PAGE'S FAILURE
 *  ISOLATION. Five sections are computed separately on the server and one failing must not blank
 *  the other four — `dashboard.schema.ts`'s per-tile failure, one level coarser. A section that
 *  could not be computed says so; it never arrives as zeros, because a zero here is a claim that
 *  nobody used the system.
 *
 *  ⚠️ A NULL INSIDE A SECTION IS A DIFFERENT ANSWER FROM A FAILED SECTION. The unique-officer and
 *  session counts are HyperLogLog unions computed on Redis; with Redis down they are `null` while
 *  the rest of the overview, which comes from Mongo, still stands.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** ⚠️ No `sections=` parameter: nothing would ever send one, and a parameter nobody sends is dead tooling. */
export const analyticsQuerySchema = z.object({
  window: z.enum(ANALYTICS_WINDOWS).default(ANALYTICS_DEFAULT_WINDOW),
});

export const ANALYTICS_SECTION_FAILURES = ['UNAVAILABLE'] as const;

/** A role as the capture records it: the verified token's role, or `none` for an anonymous request. */
export const ANALYTICS_ACTOR_ROLES = [...ACCESS_ROLES, 'none'] as const;

/**
 * ⚠️ [amended P7C §2] EXPORTED, ON THEIR SECOND USER. Duty analytics' sections fail the same way
 * and its figures are the same kind of number, so it reads these rather than declaring a twin.
 * The exported names say where they come from; `count`/`section` are kept as the local spellings.
 */
export const analyticsCountSchema = z.number().int().nonnegative();
export const analyticsSection = <T extends z.ZodType>(data: T) =>
  z.union([z.object({ data }), z.object({ failure: z.enum(ANALYTICS_SECTION_FAILURES) })]);

const count = analyticsCountSchema;
const instant = z.iso.datetime();
const section = analyticsSection;

export const analyticsOverviewSchema = z.object({
  requests: count,
  requestsBySurface: z.object({ dashboard: count, officer: count, none: count }),
  requestsByRole: z.array(z.object({ role: z.enum(ANALYTICS_ACTOR_ROLES), count })),
  /** ⚠️ HyperLogLog estimates (about 0.81% standard error), and `null` when Redis could not answer. */
  uniqueOfficers: z.object({ total: count.nullable(), dashboard: count.nullable(), officer: count.nullable() }),
  sessions: z.object({ dashboard: count.nullable(), officer: count.nullable() }),
  signIns: z.object({ dashboard: count, officer: count }),
  series: z.array(z.object({ at: instant, requests: count })),
});

export const analyticsUsageHoursSchema = z.object({
  /** `cells[weekday][hour]`: Monday is 0 and Sunday 6; the hour is the IST hour. */
  cells: z.array(z.array(count).length(24)).length(7),
  /** How many IST dates of each weekday the window covers, so a cell can be read as a daily average. */
  datesPerWeekday: z.array(count).length(7),
});

export const analyticsGeographySchema = z.object({
  /**
   * ⚠️ [amended C5b §4] Whether the geo database OPENED in this process — never merely whether one is
   * configured: `LOGIN_GEO_PROVIDER=local` with a missing or unreadable file is `false`. Requests
   * rolled up while it was not open are `unresolvedRequests`. The page shows no place at all while
   * it is `false`, including places rolled up while it was on.
   */
  lookupEnabled: z.boolean(),
  places: z.array(
    z.object({
      city: z.string().nullable(),
      state: z.string().nullable(),
      country: z.string().nullable(),
      requests: count,
    }),
  ),
  otherRequests: count,
  unresolvedRequests: count,
});

export const analyticsApiHealthSchema = z.object({
  statusClasses: z.object({ s2xx: count, s3xx: count, s4xx: count, s5xx: count, aborted: count }),
  topCodes: z.array(z.object({ code: z.string(), count })),
  errorSeries: z.array(z.object({ at: instant, requests: count, clientErrors: count, serverErrors: count })),
  /**
   * ⚠️ INTERPOLATED INSIDE A HISTOGRAM BUCKET, NOT MEASURED. `overflow` means the percentile fell
   * in the unbounded top bucket, where the value given is that bucket's LOWER bound — "at least".
   */
  latency: z.object({
    p50Ms: z.number().nonnegative().nullable(),
    p95Ms: z.number().nonnegative().nullable(),
    overflow: z.boolean(),
  }),
  slowestRoutes: z.array(
    z.object({ route: z.string(), requests: count, p95Ms: z.number().nonnegative(), overflow: z.boolean() }),
  ),
  mostErroredRoutes: z.array(
    z.object({ route: z.string(), requests: count, clientErrors: count, serverErrors: count }),
  ),
});

const failureKind = z.enum(ANALYTICS_AUTH_FAILURE_KINDS);

export const analyticsSecuritySchema = z.object({
  /**
   * [C5b §4] The same fact as `analyticsGeographySchema.lookupEnabled`, read once per report. While it
   * is `false` no address is looked up and every `place` is `null`; the page then says nothing about a
   * place, rather than "unknown" beside every address.
   */
  lookupEnabled: z.boolean(),
  topIps: z.array(z.object({ ip: z.string(), requests: count, rateLimited: count, place: z.string().nullable() })),
  otherIpRequests: count,
  authFailuresByKind: z.array(z.object({ kind: failureKind, count, routine: z.boolean() })),
  ipClusters: z.array(
    z.object({ ip: z.string(), failures: count, kinds: z.array(z.object({ kind: failureKind, count })) }),
  ),
  subnetClusters: z.array(z.object({ subnet: z.string(), failures: count, ips: count })),
  reuseRevocations: z.object({ total: count, series: z.array(z.object({ at: instant, count })) }),
  otpBans: z.object({ setInWindow: count, activeNow: count.nullable() }),
  /**
   * ⚠️ The instant from which every row the window read still holds its per-address detail — the
   * end of the newest row whose addresses the retention rule stripped. The rollup strips a row
   * whole, so this can be up to a day later than "now minus the retention". `null` when no row read
   * has been stripped.
   */
  ipDetailAvailableFrom: instant.nullable(),
});

export const analyticsReportSchema = z.object({
  window: z.enum(ANALYTICS_WINDOWS),
  from: instant,
  to: instant,
  generatedAt: instant,
  seriesGrain: z.enum(ANALYTICS_GRAINS),
  coverage: z.object({
    minutesExpected: count,
    /** ⚠️ Minutes the tiers read actually hold. A shortfall is a GAP — never read it as quiet minutes. */
    minutesCaptured: count,
    dataThrough: instant.nullable(),
    dropped: count,
  }),
  overview: section(analyticsOverviewSchema),
  usageHours: section(analyticsUsageHoursSchema),
  geography: section(analyticsGeographySchema),
  apiHealth: section(analyticsApiHealthSchema),
  security: section(analyticsSecuritySchema),
});
